import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Shield, Unlock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import api from '@/lib/api';

const SEVERITY_COLOR = {
  info: 'bg-blue-500/15 text-blue-600',
  warn: 'bg-amber-500/15 text-amber-600',
  freeze: 'bg-orange-500/15 text-orange-600',
  lockout: 'bg-red-500/15 text-red-600',
};

const EVENT_LABEL = {
  ai_quota_exceeded: 'AI quota exceeded',
  ai_circuit_breaker: 'AI circuit breaker tripped',
  auth_ip_burst: 'Auth: IP burst',
  auth_credential_stuffing: 'Auth: credential stuffing',
  exfil_burst: 'Data exfil burst',
};

function fmt(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function AbuseEvents() {
  const [events, setEvents] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, st] = await Promise.all([
        api.get('/admin/abuse/events', { params: showResolved ? {} : { resolved: false } }),
        api.get('/admin/abuse/user-states'),
      ]);
      setEvents(ev.data?.events || []);
      setStates(st.data?.states || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load abuse data');
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/abuse/events/${id}/resolve`);
      toast.success('Event resolved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resolve');
    }
  };

  const unlock = async (userId) => {
    try {
      await api.post(`/admin/abuse/users/${userId}/unlock`);
      toast.success('User unlocked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unlock');
    }
  };

  const unresolved = events.filter((e) => !e.resolved).length;

  return (
    <div className="space-y-6" data-testid="abuse-events-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Shield className="w-6 h-6" /> Abuse Protection
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Anomaly events (AI cost, auth abuse, exfil bursts) and graduated user response state.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={unresolved ? 'destructive' : 'secondary'} data-testid="abuse-unresolved-count">
            {unresolved} unresolved
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setShowResolved((v) => !v)} data-testid="abuse-toggle-resolved">
            {showResolved ? 'Hide resolved' : 'Show resolved'}
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="abuse-refresh">Refresh</Button>
        </div>
      </div>

      <Tabs defaultValue="events">
        <TabsList>
          <TabsTrigger value="events" data-testid="tab-events">Events ({events.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-user-states">Restricted users ({states.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detected events</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : events.length === 0 ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" /> No abuse events. All clear.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Detail</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((e) => (
                      <TableRow key={e.id || `${e.event_type}-${e.created_at}`} data-testid={`abuse-event-row`}>
                        <TableCell className="text-xs">{fmt(e.created_at)}</TableCell>
                        <TableCell className="font-medium">{EVENT_LABEL[e.event_type] || e.event_type}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SEVERITY_COLOR[e.severity] || 'bg-muted'}`}>
                            {e.severity}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">{e.user_email || e.user_id || '—'}</TableCell>
                        <TableCell className="text-xs">{e.ip_address || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate" title={e.detail}>
                          {e.detail || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {e.resolved ? (
                            <span className="text-xs text-green-600">Resolved</span>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => resolve(e.id)} data-testid="abuse-resolve-btn">
                              Mark resolved
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Users under graduated response</CardTitle>
            </CardHeader>
            <CardContent>
              {states.length === 0 ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" /> No users currently warned, frozen, or locked.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Offences</TableHead>
                      <TableHead>Last event</TableHead>
                      <TableHead>Frozen until</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {states.map((s) => (
                      <TableRow key={s.user_id} data-testid="abuse-state-row">
                        <TableCell className="text-xs">{s.email || s.user_id}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SEVERITY_COLOR[s.status === 'locked' ? 'lockout' : s.status === 'frozen' ? 'freeze' : 'warn']}`}>
                            {s.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">{s.offence_count}</TableCell>
                        <TableCell className="text-xs">{fmt(s.last_event_at)} <span className="text-muted-foreground">({s.last_event})</span></TableCell>
                        <TableCell className="text-xs">{fmt(s.frozen_until)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => unlock(s.user_id)} data-testid="abuse-unlock-btn">
                            <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Graduated response policy
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <p>1st offence → user gets a warning (logged, no block).</p>
          <p>2nd offence → user frozen for 1 hour (all API calls return 429).</p>
          <p>3rd offence → user fully locked out until a Super Admin clicks Unlock.</p>
          <p className="pt-2">Circuit breaker: if daily estimated org LLM spend exceeds the configured cap (default $50), <em>all</em> new AI requests pause org-wide until the next UTC day.</p>
        </CardContent>
      </Card>
    </div>
  );
}
